import { Flex, Title, Text, Button, Paper, Image, Box } from "@mantine/core";

import { useState } from "react";
import { DateInput } from "@mantine/dates";
import { FormattedMessage, useIntl } from "react-intl";
import { usePOAStepsStore } from "../../../hooks/ui/poaStepsDataStore";
import { useUpdateCustomerData } from "../../../hooks/ui/customerDataStore";
import FullmaktskollenImage from "/fullmaktskollen-logo.png";
import CheckIcon from "../../../assets/check-icon.svg?react";
import { FooterButtons } from "../../footerButtons";
import buttonClasses from "../../../styles/buttons.module.css";
import { APP_POA_STEP, trackEvent } from "../../../utils/statsig";

export default function POADuration() {
  const [durationSelection, setDurationSelection] = useState<
    string | undefined
  >("ongoing");
  const [toDate, setToDate] = useState<Date | null>(null);
  const intl = useIntl();
  const { nextStep, prevStep, app } = usePOAStepsStore();
  const { updateValidFrom, updateValidTo } = useUpdateCustomerData();

  const selectDuration = (selection: "ongoing" | "set-date") => {
    setDurationSelection(selection);
    if (app) {
      trackEvent("pension_app_poa_duration_selected", {
        duration: selection === "ongoing" ? "until_further_notice" : "end_date",
      });
    }
  };

  const canProceed =
    !durationSelection || (!toDate && durationSelection === "set-date");

  const handleNext = () => {
    updateValidFrom(new Date().toISOString());
    if (durationSelection === "ongoing") {
      updateValidTo(null);
    } else if (durationSelection === "set-date") {
      const tDate = toDate?.setHours(23, 59, 59, 999);

      updateValidTo(tDate ? new Date(tDate).toISOString() : null);
    } else {
      throw new Error("Invalid duration selection");
    }

    if (app) {
      trackEvent("pension_app_poa_next_clicked", {
        step: APP_POA_STEP.duration,
      });
    }
    nextStep();
  };

  return (
    <Flex justify="center" align="center">
      <Flex direction="column" maw={672} w={"100%"}>
        <Paper
          p={app ? 0 : "xl"}
          radius={"24px"}
          mb={durationSelection === "set-date" ? 0 : "xl"}
        >
          <Title
            order={2}
            mb="1rem"
            ff={"var(--wallet-font-family-sambla-sans)"}
          >
            <FormattedMessage
              id="poaDuration.title"
              defaultMessage="Hur länge vill du att fullmakten ska gälla?"
            />
          </Title>
          <Text mb={"1rem"}>
            <FormattedMessage
              id="poaDuration.titleParagraph"
              defaultMessage="Fullmakten ger oss möjlighet att kontinuerligt se över din pension och komma med råd på hur du kan förbättra ditt sparande. Hur länge vill du att vi ska ha tillgång till din fullmakt? Vi rekommenderar att vi gör detta tills vidare."
            />
          </Text>
          <Box
            role="group"
            aria-label="Hur länge vill du att fullmakten ska gälla?"
          >
            <Flex direction={"column"} gap={"sm"} mb={"1rem"}>
              <Button
                fullWidth
                aria-pressed={durationSelection === "ongoing"}
                leftSection={
                  durationSelection === "ongoing" ? <CheckIcon /> : null
                }
                onClick={() => selectDuration("ongoing")}
              >
                <FormattedMessage
                  id="poaDuration.option.continously"
                  defaultMessage="Tills vidare"
                />
              </Button>
              <Button
                variant="default"
                bg="var(--wallet-color-masterbrand-accent-beige)"
                className={buttonClasses.borderBottomOnly}
                fullWidth
                aria-pressed={durationSelection === "set-date"}
                leftSection={
                  durationSelection === "set-date" ? <CheckIcon /> : null
                }
                onClick={() => selectDuration("set-date")}
              >
                <FormattedMessage
                  id="poaDuration.option.setDate"
                  defaultMessage="Until a set date"
                />
              </Button>
            </Flex>
          </Box>
          {durationSelection === "set-date" && (
            <DateInput
              value={toDate}
              onChange={setToDate}
              placeholder={intl.formatMessage({
                id: "poaDuration.option.setDate.placeholder",
                defaultMessage: "Välj ett datum",
              })}
              aria-label={intl.formatMessage({
                id: "poaDuration.option.setDate.placeholder",
                defaultMessage: "Välj ett datum",
              })}
              minDate={new Date(new Date().getTime() + 24 * 60 * 60 * 1000)}
            />
          )}
        </Paper>
        <Paper p={"lg"} radius={"24px"} mb={"xl"}>
          <Flex justify={"center"}>
            <Image
              src={FullmaktskollenImage}
              alt="Fullmaktskollen"
              h={38}
              w="auto"
              fit="contain"
            />
          </Flex>
        </Paper>

        <FooterButtons app={app}>
          <Button
            onClick={() => {
              if (app) {
                trackEvent("pension_app_poa_back_clicked", {
                  step: APP_POA_STEP.duration,
                });
              }
              prevStep();
            }}
            variant="default"
          >
            <FormattedMessage id="bottomNav.back" defaultMessage="Tillbaka" />
          </Button>
          <Button onClick={() => handleNext()} disabled={canProceed}>
            <FormattedMessage id="bottomNav.next" defaultMessage="Nästa" />
          </Button>
        </FooterButtons>
      </Flex>
    </Flex>
  );
}
